import type * as React from 'react';
import { useEffect, useRef } from 'react';

import { cn } from '../../styles/lib/utils';
import { getNextTabbable } from '../../utils/getTabbables';
import { AlertBar } from '../AlertBar';
import { Button } from '../Button';

export type ExportLimitsStatusState = 'idle' | 'loading' | 'loaded' | 'failed';

type Props = React.ComponentProps<'div'> & {
  status: ExportLimitsStatusState;
  checkingLabel: string;
  unavailableTitle: string;
  unavailableDescription: string;
  retryLabel: string;
  onRetry: () => void;
  retryButtonTestId?: string;
};

/** Stays mounted while empty so screen readers announce each message as it is inserted. */
export function ExportLimitsStatus({
  status,
  checkingLabel,
  unavailableTitle,
  unavailableDescription,
  retryLabel,
  onRetry,
  retryButtonTestId,
  className,
  ...props
}: Props) {
  const regionRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const retryPending = useRef(false);

  useEffect(() => {
    if (status === 'loading' || status === 'idle' || !retryPending.current) return;
    retryPending.current = false;
    if (status === 'failed') {
      retryRef.current?.focus();
    } else if (regionRef.current) {
      // The region empties once loaded, so focus moves on rather than resting on nothing
      getNextTabbable(regionRef.current)?.focus();
    }
  }, [status]);

  const handleRetry = () => {
    retryPending.current = true;
    regionRef.current?.focus();
    onRetry();
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> is for form results, not a status message
    <div
      ref={regionRef}
      role="status"
      aria-live="polite"
      tabIndex={-1}
      data-slot="export-limits-status"
      data-state={status}
      className={cn('font-noto text-xs outline-none', className)}
      {...props}
    >
      {status === 'loading' && <p className="text-grey-80">{checkingLabel}</p>}
      {status === 'failed' && (
        <AlertBar variant="warning" icon="warning" title={unavailableTitle}>
          <div className="flex flex-col items-center gap-2">
            <p>{unavailableDescription}</p>
            <Button
              ref={retryRef}
              type="button"
              variant="secondary"
              onClick={handleRetry}
              data-testid={retryButtonTestId}
            >
              {retryLabel}
            </Button>
          </div>
        </AlertBar>
      )}
    </div>
  );
}
